import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

interface TireDepthSectionProps {
  tireDepth: {
    sameForAll?: boolean;
    mainDepth?: string;
    frontDepth?: string;
    rearDepth?: string;
    hasExtraSet?: boolean;
    extraDepth?: string;
    extraFrontDepth?: string;
    extraRearDepth?: string;
  } | null;
  onTireDepthChange: (tireDepth: any) => void;
  onNext: () => void;
  isCreatingTest?: boolean;
}

export const TireDepthSection = ({
  tireDepth,
  onTireDepthChange,
  onNext,
  isCreatingTest = false,
}: TireDepthSectionProps) => {
  const { language } = useLanguage();
  const vt = listingPostLabels(language).vehicleTest;
  const [sameForAll, setSameForAll] = useState(tireDepth?.sameForAll ?? true);
  // Use tireDepth.hasExtraSet directly as the source of truth, but maintain local state for immediate UI updates
  const [hasExtraSet, setHasExtraSet] = useState(
    tireDepth?.hasExtraSet ?? false,
  );

  // Track if user is manually changing the checkbox to prevent useEffect from overriding
  const isUserChangingRef = useRef(false);
  const prevHasExtraSetRef = useRef(tireDepth?.hasExtraSet);

  // Sync state with prop when tireDepth.hasExtraSet changes (e.g., when vehicleDetails.extraTireSet changes)
  // But don't override if user is currently changing it
  useEffect(() => {
    // If user is changing it, reset flag after a short delay to allow the change to propagate
    if (isUserChangingRef.current) {
      const timeoutId = setTimeout(() => {
        isUserChangingRef.current = false;
        // After flag is reset, sync state with prop if needed
        if (
          tireDepth?.hasExtraSet !== undefined &&
          tireDepth.hasExtraSet !== hasExtraSet
        ) {
          setHasExtraSet(tireDepth.hasExtraSet);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }

    // Only sync if prop actually changed (not just on every render)
    if (
      tireDepth?.hasExtraSet !== undefined &&
      tireDepth.hasExtraSet !== prevHasExtraSetRef.current
    ) {
      prevHasExtraSetRef.current = tireDepth.hasExtraSet;
      if (tireDepth.hasExtraSet !== hasExtraSet) {
        setHasExtraSet(tireDepth.hasExtraSet);
      }
    }
  }, [tireDepth?.hasExtraSet, hasExtraSet]);

  const handleDepthChange = (
    depth: string,
    type: "main" | "front" | "rear" | "extra" | "extraFront" | "extraRear",
  ) => {
    const newData = { ...tireDepth };

    if (type === "main") {
      newData.mainDepth = depth;
      if (sameForAll) {
        newData.frontDepth = depth;
        newData.rearDepth = depth;
      }
    } else if (type === "front") {
      newData.frontDepth = depth;
    } else if (type === "rear") {
      newData.rearDepth = depth;
    } else if (type === "extra") {
      newData.extraDepth = depth;
      if (sameForAll) {
        newData.extraFrontDepth = depth;
        newData.extraRearDepth = depth;
      }
    } else if (type === "extraFront") {
      newData.extraFrontDepth = depth;
    } else if (type === "extraRear") {
      newData.extraRearDepth = depth;
    }

    onTireDepthChange(newData);
  };

  const handleSameForAllChange = (checked: boolean) => {
    setSameForAll(checked);
    // Always clear all values when toggling the checkbox
    const newData = {
      ...tireDepth,
      sameForAll: checked,
      mainDepth: undefined,
      frontDepth: undefined,
      rearDepth: undefined,
      extraDepth: undefined,
      extraFrontDepth: undefined,
      extraRearDepth: undefined,
    };
    onTireDepthChange(newData);
  };

  const handleHasExtraSetChange = (checked: boolean) => {
    // Mark that user is manually changing the checkbox
    isUserChangingRef.current = true;
    setHasExtraSet(checked);
    const newData = { ...tireDepth, hasExtraSet: checked };
    if (!checked) {
      newData.extraDepth = undefined;
      newData.extraFrontDepth = undefined;
      newData.extraRearDepth = undefined;
    }
    onTireDepthChange(newData);
  };

  const depthOptions = ["<2mm", "3-4mm", "5-6mm", "7-8mm"];

  const isMainComplete =
    tireDepth?.mainDepth || (tireDepth?.frontDepth && tireDepth?.rearDepth);
  const isExtraComplete =
    !hasExtraSet ||
    tireDepth?.extraDepth ||
    (tireDepth?.extraFrontDepth && tireDepth?.extraRearDepth);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-sm mb-3">{vt.treadDepthOnTires}</h4>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sameForAll"
              checked={sameForAll}
              onCheckedChange={handleSameForAllChange}
            />
            <label htmlFor="sameForAll" className="text-sm">
              {vt.sameForAllFour}
            </label>
          </div>

          {sameForAll ? (
            <div className="space-y-3">
              <h5 className="font-medium text-sm">{vt.treadDepth}</h5>
              <div className="grid grid-cols-2 gap-1.5">
                {depthOptions.map((depth) => (
                  <Button
                    key={depth}
                    onClick={() => handleDepthChange(depth, "main")}
                    variant="formOption"
                    className={`${
                      tireDepth?.mainDepth === depth
                        ? "border-accent border-2"
                        : "border-gray-400"
                    }`}
                  >
                    {depth}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                <h5 className="font-medium text-sm">{vt.frontTires}</h5>
                <div className="grid grid-cols-2 gap-1.5">
                  {depthOptions.map((depth) => (
                    <Button
                      key={depth}
                      onClick={() => handleDepthChange(depth, "front")}
                      variant="formOption"
                      className={`${
                        tireDepth?.frontDepth === depth
                          ? "border-accent border-2"
                          : "border-gray-400"
                      }`}
                    >
                      {depth}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h5 className="font-medium text-sm">{vt.rearTires}</h5>
                <div className="grid grid-cols-2 gap-1.5">
                  {depthOptions.map((depth) => (
                    <Button
                      key={depth}
                      onClick={() => handleDepthChange(depth, "rear")}
                      variant="formOption"
                      className={`${
                        tireDepth?.rearDepth === depth
                          ? "border-accent border-2"
                          : "border-gray-400"
                      }`}
                    >
                      {depth}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mt-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="hasExtraSet"
              checked={tireDepth?.hasExtraSet ?? hasExtraSet}
              onCheckedChange={handleHasExtraSetChange}
            />
            <label htmlFor="hasExtraSet" className="text-sm">
              {vt.hasExtraTireSet}
            </label>
          </div>

          {(tireDepth?.hasExtraSet ?? hasExtraSet) && (
            <div className="space-y-4">
              {sameForAll ? (
                <div className="space-y-3">
                  <h5 className="font-medium text-sm">
                    {vt.extraSetTreadDepth}
                  </h5>
                  <div className="grid grid-cols-2 gap-1.5">
                    {depthOptions.map((depth) => (
                      <Button
                        key={depth}
                        onClick={() => handleDepthChange(depth, "extra")}
                        variant="formOption"
                        className={`${
                          tireDepth?.extraDepth === depth
                            ? "border-accent border-2"
                            : "border-gray-400"
                        }`}
                      >
                        {depth}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <h5 className="font-medium text-sm">
                      {vt.extraSetFrontTires}
                    </h5>
                    <div className="grid grid-cols-2 gap-1.5">
                      {depthOptions.map((depth) => (
                        <Button
                          key={depth}
                          onClick={() => handleDepthChange(depth, "extraFront")}
                          variant="formOption"
                          className={`${
                            tireDepth?.extraFrontDepth === depth
                              ? "border-accent border-2"
                              : "border-gray-400"
                          }`}
                        >
                          {depth}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h5 className="font-medium text-sm">
                      {vt.extraSetRearTires}
                    </h5>
                    <div className="grid grid-cols-2 gap-1.5">
                      {depthOptions.map((depth) => (
                        <Button
                          key={depth}
                          onClick={() => handleDepthChange(depth, "extraRear")}
                          variant="formOption"
                          className={`${
                            tireDepth?.extraRearDepth === depth
                              ? "border-accent border-2"
                              : "border-gray-400"
                          }`}
                        >
                          {depth}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isMainComplete && isExtraComplete && (
          <Button
            className="w-full mt-6"
            onClick={onNext}
            disabled={isCreatingTest}
          >
            {isCreatingTest ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {vt.creatingTest}
              </>
            ) : (
              <>{vt.next}</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
